// /mnt/mantenimientos/frontend/src/utils/zabbix.js

/* ================================
   FORMATOS BÁSICOS
================================ */

export function fmtClock(unix) {
  const t = Number(unix);
  if (!Number.isFinite(t) || t <= 0) return "—";
  try {
    return new Date(t * 1000).toLocaleString("es-ES");
  } catch {
    return String(t);
  }
}

export function fmtUptime(seconds) {
  const s = Number(seconds || 0);
  if (!Number.isFinite(s) || s < 0) return "—";

  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);

  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function pct(v, digits = 1) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

export function gb(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)} GB`;
}

export function toMbps(bps, digits = 2) {
  const n = Number(bps);
  if (!Number.isFinite(n)) return null;
  return Number((n / 1_000_000).toFixed(digits));
}

export function staleMinutes(lastclock) {
  const lc = Number(lastclock || 0);
  if (!Number.isFinite(lc) || lc <= 0) return null;
  const now = Math.floor(Date.now() / 1000);
  return Math.max(0, Math.round((now - lc) / 60));
}

export function formatAgeFromClock(clock) {
  const minutes = staleMinutes(clock);
  if (minutes === null) return "Sin datos";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

export function simplifyOs(value) {
  const text = String(value || "").trim();
  if (!text) return "Sin identificar";
  if (/pfsense/i.test(text)) return "pfSense";
  const windowsServer = text.match(/Windows Server\s+\d{4}/i);
  if (windowsServer) return windowsServer[0];
  const windows = text.match(/Windows\s+(?:11|10|8(?:\.1)?|7)/i);
  if (windows) return windows[0];
  if (/openmediavault/i.test(text)) return "OpenMediaVault";
  if (/freebsd/i.test(text)) return "FreeBSD";
  if (/ubuntu/i.test(text)) return "Ubuntu Linux";
  if (/debian/i.test(text)) return "Debian Linux";
  if (/linux/i.test(text)) return "Linux";
  if (/mac\s?os|darwin/i.test(text)) return "macOS";
  return text.length > 64 ? `${text.slice(0, 61)}…` : text;
}

export function serviceState(label) {
  const value = String(label || "")
    .trim()
    .toLowerCase();
  if (value === "running") return { kind: "ok", label: "Correcto" };
  if (value === "stopped") return { kind: "bad", label: "Detenido" };
  if (value === "not_found") return { kind: "na", label: "No aplicable" };
  if (!value) return { kind: "unknown", label: "Sin datos" };
  return { kind: "warn", label: "Revisar" };
}

export function severityInfo(value) {
  const severity = Number(value || 0);
  if (severity >= 5) return { kind: "bad", label: "Desastre", severity };
  if (severity === 4) return { kind: "bad", label: "Alta", severity };
  if (severity === 3) return { kind: "warn", label: "Media", severity };
  if (severity === 2) return { kind: "warn", label: "Aviso", severity };
  return {
    kind: "muted",
    label: severity === 1 ? "Información" : "Sin clasificar",
    severity,
  };
}

export function monitoringHealth(zabbixEntry) {
  const z = normalizeZabbixEntry(zabbixEntry);
  const m = z.metricas;
  if (!m || !Number(m.lastclock)) {
    return { kind: "unknown", label: "Sin datos", priority: 3 };
  }

  const agentRaw = m.agent_ping;
  const agentDown =
    agentRaw !== null &&
    agentRaw !== undefined &&
    agentRaw !== "" &&
    Number(agentRaw) !== 1;
  const stale = staleMinutes(m.lastclock);
  const diskFree = worstDiskFreePct(z.disks);
  const maxSeverity = z.problems.reduce(
    (max, problem) => Math.max(max, Number(problem?.severity || 0)),
    0,
  );
  const stoppedServices = z.services.filter(
    (service) => serviceState(service.label).kind === "bad",
  ).length;

  if (
    agentDown ||
    maxSeverity >= 4 ||
    (Number.isFinite(diskFree) && diskFree < 10)
  ) {
    return { kind: "bad", label: "Crítico", priority: 0 };
  }
  if (
    z.problems.length > 0 ||
    stoppedServices > 0 ||
    (Number.isFinite(diskFree) && diskFree < 20) ||
    (stale !== null && stale >= 60)
  ) {
    return { kind: "warn", label: "Revisar", priority: 1 };
  }
  return { kind: "ok", label: "Operativo", priority: 2 };
}

/* ================================
   DISCO
================================ */

export function worstDiskFreePct(disks = []) {
  const arr = Array.isArray(disks) ? disks : [];
  let worst = null;

  for (const d of arr) {
    const used = Number(d?.used_pct);
    if (!Number.isFinite(used)) continue;
    const free = 100 - used;
    if (worst === null || free < worst) worst = free;
  }

  return worst;
}

/* ================================
   DETECCIÓN DE TIPO DE EQUIPO
================================ */

export function isPfSense(entryOrMetricas) {
  if (!entryOrMetricas || typeof entryOrMetricas !== "object") return false;

  const root = entryOrMetricas;
  const metricas =
    root.metricas && typeof root.metricas === "object" ? root.metricas : root;

  const os = String(metricas?.os || "").toLowerCase();
  const host = String(root?.host || "").toLowerCase();

  // Interfaces pueden venir en metricas.interfaces o root.interfaces según endpoint
  const interfacesA = Array.isArray(metricas?.interfaces)
    ? metricas.interfaces
    : [];
  const interfacesB = Array.isArray(root?.interfaces) ? root.interfaces : [];
  const hasInterfacesData = interfacesA.length + interfacesB.length > 0;

  // Señales fuertes
  if (os.includes("pfsense")) return true;
  if (host.includes("pfsense")) return true;

  // Señal secundaria (si OS no trae pfSense pero hay interfaces y OS suena a FreeBSD)
  if (hasInterfacesData && os.includes("freebsd")) return true;

  return false;
}

/* ================================
   INTERFACES PF SENSE
================================ */

function ifaceRoleRank(role) {
  const r = String(role || "").toLowerCase();
  if (r === "wan") return 1;
  if (r === "lan") return 2;
  if (r === "opt") return 3;
  if (r === "vpn") return 4;
  return 9;
}

function roleFromDescr(ifdescr = "") {
  const d = String(ifdescr || "")
    .trim()
    .toLowerCase();
  if (!d) return "other";
  if (d === "wan" || d.includes("wan") || d.includes("internet")) return "wan";
  if (d === "lan" || d.includes("lan")) return "lan";
  if (d.startsWith("opt") || d.includes("opt")) return "opt";
  if (d.includes("vpn") || d.includes("openvpn") || d.includes("ipsec"))
    return "vpn";
  return "other";
}

function prettyIfaceName(i) {
  const descr = String(i?.ifdescr || "").trim();
  const name = String(i?.ifname || "").trim();
  return descr || name || "—";
}

export function filterPfSenseInterfaces(interfaces = [], opts = {}) {
  const {
    hide = ["enc0", "pflog0", "pfsync0", "lo0", "lo"],
    onlyActive = false,
  } = opts;

  const hidden = new Set(hide.map(String));
  let list = Array.isArray(interfaces) ? interfaces : [];

  // Normaliza (asegura role si viene vacío pero hay ifdescr)
  list = list
    .filter((i) => i && typeof i === "object")
    .map((i) => {
      const ifname = String(i.ifname || "").trim();
      const ifdescr = String(i.ifdescr || "").trim();
      const role = String(i.role || "").trim() || roleFromDescr(ifdescr);
      return { ...i, ifname, ifdescr, role };
    });

  // Quitar interfaces técnicas por ifname
  list = list.filter((i) => i.ifname && !hidden.has(String(i.ifname)));

  if (onlyActive) {
    list = list.filter((i) => i.active === true);
  }

  // Orden universal: WAN → LAN → OPT → VPN → OTHER; luego por nombre (descr) y ifname
  list = [...list].sort((a, b) => {
    const ra = ifaceRoleRank(a?.role);
    const rb = ifaceRoleRank(b?.role);
    if (ra !== rb) return ra - rb;

    const na = prettyIfaceName(a);
    const nb = prettyIfaceName(b);
    const c = String(na).localeCompare(String(nb));
    if (c !== 0) return c;

    return String(a.ifname).localeCompare(String(b.ifname));
  });

  return list;
}

export function summarizeInterface(i) {
  const inM = toMbps(i?.in_bps);
  const outM = toMbps(i?.out_bps);

  const errors = (Number(i?.in_errors) || 0) + (Number(i?.out_errors) || 0);

  const drops = (Number(i?.in_dropped) || 0) + (Number(i?.out_dropped) || 0);

  const label = (() => {
    const descr = String(i?.ifdescr || "").trim();
    const name = String(i?.ifname || "").trim();
    if (descr && name && descr !== name) return `${descr} · ${name}`;
    return descr || name || "—";
  })();

  return {
    label,
    inMbps: inM,
    outMbps: outM,
    errors,
    drops,
    hasWarn: errors > 0 || drops > 0,
  };
}

/* ================================
   NORMALIZADOR GLOBAL
================================ */

export function normalizeZabbixEntry(zabbixEntry) {
  const root =
    zabbixEntry && typeof zabbixEntry === "object" ? zabbixEntry : null;

  const metricas =
    root && root.metricas && typeof root.metricas === "object"
      ? root.metricas
      : root;

  const servicesObj =
    metricas?.services && typeof metricas.services === "object"
      ? metricas.services
      : {};

  const services = Object.entries(servicesObj).map(([name, obj]) => {
    const value = obj && typeof obj === "object" ? obj.value : obj;
    const label =
      obj && typeof obj === "object" ? obj.label : String(value ?? "");
    return { name, value, label };
  });

  const disks = Array.isArray(metricas?.disks) ? metricas.disks : [];

  const interfaces = Array.isArray(metricas?.interfaces)
    ? metricas.interfaces
    : [];

  const problems = Array.isArray(metricas?.problems)
    ? metricas.problems
    : Array.isArray(root?.top_problems)
      ? root.top_problems
      : [];

  const lastclock = metricas?.lastclock ?? root?.lastclock ?? null;

  return {
    root,
    metricas: metricas && typeof metricas === "object" ? metricas : null,
    host: root?.host || "",
    hostid: root?.hostid || "",
    services,
    disks,
    interfaces,
    problems,
    lastclock,
  };
}
